/**
 * Toast helpers for the trading mutation hooks — relocated from
 * frontend/src/queries/transactions.ts:310-491.
 *
 * Each helper is pure over the `TradeMutationDeps` slice it needs (toast +
 * translator + share + thesis-dialog + explorer URL). The SDK orchestrates
 * the toast lifecycle (submitting → confirmed | failed | cancelled); the
 * host owns the vendor bindings.
 */

import type { TxErrorInfo } from '../../../../tx-engine/index.js';
import {
  type ShareTradeRequest,
  type ShowToastInput,
  type ThesisTradeContext,
  TRADING_TOAST_IDS,
  type TradeMutationDeps,
  type TradeShareContext,
  type TxTranslator,
} from '../types.js';
import { formatTxLabel, getRecoveryDescription, getToastId, resolveErrorMessage } from './trace.js';

// ── Deps slice the toasts need ───────────────────────────────────────────────

type ToastDeps = Pick<
  TradeMutationDeps,
  | 't'
  | 'showToast'
  | 'dismissToast'
  | 'getExplorerUrl'
  | 'openExternalUrl'
  | 'formatBalanceSigned'
  | 'formatShareD6'
> &
  Pick<TradeMutationDeps, 'thesis' | 'tradeSharing'>;

function getThesisTradeContext(
  direction: 'buy' | 'sell' | 'swap',
  shareCtx: TradeShareContext | undefined,
): ThesisTradeContext | null {
  if (shareCtx === undefined || (direction !== 'buy' && direction !== 'sell')) return null;

  return {
    outcomeIndex: shareCtx.outcomeIndex,
    direction,
    amountD6: shareCtx.amountD6,
    outcomeLabel: null,
  };
}

// ── Submitting toast (cancellable) ───────────────────────────────────────────

export function showSubmittingToast(
  deps: ToastDeps,
  traceId: string,
  direction: 'buy' | 'sell' | 'swap',
  txNum: number,
  formattedAmount: string,
  attempt: number,
  maxAttempts: number,
  abortController?: AbortController,
): void {
  const toastId = getToastId(TRADING_TOAST_IDS.TX_SUBMITTING, traceId);
  const label = formatTxLabel(txNum, formattedAmount);
  const isRetry = attempt > 1;
  const message = isRetry
    ? deps.t('toast.submittingDirectionRetry', {
        direction,
        label,
        attempt: String(attempt),
        max: String(maxAttempts),
      })
    : deps.t('toast.submittingDirection', { direction, label });

  const params: ShowToastInput = {
    id: toastId,
    message,
    variant: 'loading',
    duration: Number.POSITIVE_INFINITY,
  };
  if (abortController !== undefined) {
    params.cancel = {
      label: deps.t('toast.cancelButton'),
      onClick: (): void => {
        abortController.abort();
      },
    };
  }
  deps.showToast(params);
}

// ── Success toast (with thesis / share actions) ──────────────────────────────

export function showSuccessToast(
  deps: ToastDeps,
  traceId: string,
  direction: 'buy' | 'sell' | 'swap',
  txNum: number,
  formattedAmount: string,
  signature: string,
  marketAddress?: string,
  walletAddress?: string,
  shareCtx?: TradeShareContext,
  realizedPnlD6?: string | null,
  marketTitle?: string,
  // Offer the post-trade "Add thesis" action. Pass false for graduated/AMM
  // (winner) trades — theses live on market detail only for now (MOR-729). The
  // Share action lives in a separate slot, so it is unaffected.
  offerThesis: boolean = true,
): void {
  const submittingId = getToastId(TRADING_TOAST_IDS.TX_SUBMITTING, traceId);
  deps.dismissToast(submittingId);

  // A NATS early-confirm for a graduated (AMM) trade lands success before any
  // signature is known (AmmUserActivity carries none). Show "confirmed" now
  // and skip the signature-dependent actions; they appear when Turnkey
  // reconciles the authoritative signature (re-fires this toast in place).
  const hasSignature = signature.length > 0;
  const label = formatTxLabel(txNum, formattedAmount);
  const capitalDirection = `${direction.charAt(0).toUpperCase()}${direction.slice(1)}`;
  // Direction is 'buy' | 'sell' | 'swap'. Only buy/sell map cleanly onto the
  // thesis trade-pill UX (a single entry direction with a USDC amount).
  // For swap we leave tradeContext null and fall back to the generic prompt.
  const thesisTradeContext = getThesisTradeContext(direction, shareCtx);

  const thesis = deps.thesis;
  const actionConfig = !hasSignature
    ? undefined
    : marketAddress !== undefined && walletAddress !== undefined
      ? thesis !== undefined && offerThesis
        ? {
            label: deps.t('toast.addThesis'),
            onClick: (): void => {
              thesis.openDialog(
                marketAddress,
                signature,
                walletAddress,
                thesisTradeContext,
                traceId,
              );
            },
          }
        : // Graduated/AMM (winner) trade or a host without theses: no action.
          undefined
      : {
          label: deps.t('toast.viewExplorer'),
          onClick: (): void => {
            deps.openExternalUrl(deps.getExplorerUrl(signature));
          },
        };

  // Share button — sonner renders `cancel` next to `action`, so the toast
  // ends up with `[Add thesis] [Share]`. Only wired when we have the full
  // trade context (buy/sell). Swap skips this for v1 because it doesn't have
  // a clean USDC amount to feature in the OG hero. Pops the global
  // ShareModal so the toast can fire-and-forget — the toast unmounts shortly
  // after but the modal lives in its own portal.
  const tradeSharing = deps.tradeSharing;
  const shareConfig =
    hasSignature &&
    shareCtx !== undefined &&
    marketAddress !== undefined &&
    walletAddress !== undefined &&
    tradeSharing !== undefined &&
    (direction === 'buy' || direction === 'sell' || direction === 'swap')
      ? {
          label: deps.t('toast.shareTrade'),
          onClick: (): void => {
            const sharePath = tradeSharing.buildUrl(signature, {
              marketPubkey: marketAddress,
              userPubkey: walletAddress,
              direction,
              amountD6: shareCtx.amountD6,
              outcomeIndex: shareCtx.outcomeIndex,
            });
            const url = `${tradeSharing.getBaseUrl()}${sharePath}`;
            // Sells with a known market + realised PnL get the richer copy
            // (market name + signed PnL). Buys/swaps and cost-basis-unknown
            // sells fall back to the generic direction+amount copy.
            const text =
              direction === 'sell' &&
              marketTitle !== undefined &&
              realizedPnlD6 !== undefined &&
              realizedPnlD6 !== null
                ? deps.t('share.tweetTextSellPnl', {
                    marketTitle,
                    pnl: deps.formatBalanceSigned(realizedPnlD6),
                  })
                : deps.t('share.tweetText', {
                    direction,
                    amount: formattedAmount,
                  });
            const request: ShareTradeRequest = {
              surface: {
                kind: 'trade',
                signature,
                marketPubkey: marketAddress,
                userPubkey: walletAddress,
                direction,
                amountD6: shareCtx.amountD6,
                outcomeIndex: shareCtx.outcomeIndex,
              },
              url,
              text,
              title: deps.t('share.modalTitle'),
            };
            tradeSharing.openModal(request);
          },
        }
      : undefined;

  const toastDirection =
    shareCtx !== undefined && (direction === 'buy' || direction === 'sell')
      ? `${capitalDirection} ${deps.formatShareD6(shareCtx.shareAmountD6)} shares`
      : capitalDirection;
  const message = deps.t('toast.directionConfirmed', { direction: toastDirection, label });
  const toastParams: ShowToastInput = {
    id: getToastId(TRADING_TOAST_IDS.TX_CONFIRMED, traceId),
    message,
    variant: 'success',
    duration: 5000,
  };
  if (actionConfig !== undefined) toastParams.action = actionConfig;
  if (shareConfig !== undefined) toastParams.cancel = shareConfig;
  if (actionConfig !== undefined && shareConfig !== undefined) {
    toastParams.actionLayout = 'inline';
  }
  // Realised PnL — sells only. The host renders `pnl` colour-coded (profit
  // green / loss red / dust neutral); the success variant is unchanged.
  if (direction === 'sell' && realizedPnlD6 !== undefined && realizedPnlD6 !== null) {
    toastParams.pnl = {
      amountD6: realizedPnlD6,
      formatted: deps.formatBalanceSigned(realizedPnlD6),
    };
  }
  deps.showToast(toastParams);
}

// ── Boost success toast (no thesis/share affordances; explorer-only) ────────

export function showBoostSuccessToast(
  deps: Pick<ToastDeps, 't' | 'showToast' | 'dismissToast' | 'getExplorerUrl' | 'openExternalUrl'>,
  traceId: string,
  formattedAmount: string,
  signature: string,
): void {
  deps.dismissToast(getToastId(TRADING_TOAST_IDS.TX_SUBMITTING, traceId));
  deps.showToast({
    id: getToastId(TRADING_TOAST_IDS.TX_CONFIRMED, traceId),
    message: deps.t('toast.boostConfirmed', { amount: formattedAmount }),
    variant: 'success',
    duration: 5000,
    action: {
      label: deps.t('toast.viewExplorer'),
      onClick: (): void => {
        deps.openExternalUrl(deps.getExplorerUrl(signature));
      },
    },
  });
}

// ── Boost submitting toast (uses `submittingBoost` copy) ────────────────────

export function showBoostSubmittingToast(
  deps: Pick<ToastDeps, 't' | 'showToast'>,
  traceId: string,
  formattedAmount: string,
  abortController: AbortController,
): void {
  deps.showToast({
    id: getToastId(TRADING_TOAST_IDS.TX_SUBMITTING, traceId),
    message: deps.t('toast.submittingBoost', { amount: formattedAmount }),
    variant: 'loading',
    duration: Number.POSITIVE_INFINITY,
    cancel: {
      label: deps.t('toast.cancelButton'),
      onClick: (): void => {
        abortController.abort();
      },
    },
  });
}

export function showClaimSuccessToast(
  deps: Pick<
    TradeMutationDeps,
    't' | 'showToast' | 'dismissToast' | 'getExplorerUrl' | 'openExternalUrl' | 'formatBalanceD6'
  >,
  traceId: string,
  signature: string,
  /** Exact quote credited by the claim; `null` when it could not be read. */
  payoutD6: string | null,
): void {
  deps.dismissToast(getToastId(TRADING_TOAST_IDS.TX_SUBMITTING, traceId));
  deps.showToast({
    id: getToastId(TRADING_TOAST_IDS.TX_CONFIRMED, traceId),
    message:
      payoutD6 === null
        ? deps.t('toast.confirmed')
        : deps.t('toast.claimAddedToWallet', { amount: deps.formatBalanceD6(payoutD6) }),
    variant: 'success',
    duration: 5000,
    action: {
      label: deps.t('toast.viewExplorer'),
      onClick: (): void => {
        deps.openExternalUrl(deps.getExplorerUrl(signature));
      },
    },
  });
}

export function showClaimSubmittingToast(
  deps: Pick<ToastDeps, 't' | 'showToast'>,
  traceId: string,
  abortController: AbortController,
): void {
  deps.showToast({
    id: getToastId(TRADING_TOAST_IDS.TX_SUBMITTING, traceId),
    message: deps.t('toast.signing'),
    variant: 'loading',
    duration: Number.POSITIVE_INFINITY,
    cancel: {
      label: deps.t('toast.cancelButton'),
      onClick: (): void => {
        abortController.abort();
      },
    },
  });
}

// ── Failure / cancellation ──────────────────────────────────────────────────

export function showFailureToast(
  deps: Pick<ToastDeps, 't' | 'showToast' | 'dismissToast'>,
  traceId: string,
  errorInfo: TxErrorInfo,
  onRetryWithSlippage?: (slippageBps: number) => void,
): void {
  const submittingId = getToastId(TRADING_TOAST_IDS.TX_SUBMITTING, traceId);
  deps.dismissToast(submittingId);
  // Also clear any early-success ("confirmed") toast for this trade: a NATS
  // match can flash success before an authoritative revert/failure corrects it,
  // and we must never leave a stale success toast on screen alongside failure.
  deps.dismissToast(getToastId(TRADING_TOAST_IDS.TX_CONFIRMED, traceId));

  const description = getRecoveryDescription(errorInfo.recoveryAction, deps.t);
  const resolved = resolveErrorMessage(errorInfo.userMessageKey, deps.t);

  const failedToastId = getToastId(TRADING_TOAST_IDS.TX_FAILED, traceId);
  const toastParams: ShowToastInput = {
    id: failedToastId,
    message: deps.t('toast.failedPrefix', { reason: resolved }),
    variant: 'error',
    duration: 5000,
    cancel: {
      label: deps.t('toast.closeButton'),
      onClick: (): void => {
        deps.dismissToast(failedToastId);
      },
    },
  };
  const recoveryAction = errorInfo.recoveryAction;
  if (
    recoveryAction.type === 'prompt_retry_with_higher_slippage' &&
    onRetryWithSlippage !== undefined
  ) {
    let retryStarted = false;
    toastParams.action = {
      label: deps.t('toast.retryWithSlippage', {
        slippage: String(recoveryAction.suggestedSlippageBps / 100),
      }),
      onClick: (): void => {
        if (retryStarted) return;
        retryStarted = true;
        deps.dismissToast(failedToastId);
        onRetryWithSlippage(recoveryAction.suggestedSlippageBps);
      },
    };
  }
  // Surface the recovery hint only when it adds information the resolved
  // message doesn't already carry. Most resolved errors end in "Please try
  // again." (e.g. `tx.errors.turnkeyFailed`), and the `prompt_retry` hint is
  // literally "Please try again" — showing it as the description repeats the
  // phrase twice (MOR-820). A wired slippage retry surfaces as the action;
  // otherwise distinct recovery hints remain in the description.
  if (
    toastParams.action === undefined &&
    description !== undefined &&
    !resolved.toLowerCase().includes(description.toLowerCase())
  ) {
    toastParams.description = description;
  }
  deps.showToast(toastParams);
}

export function showCancelledToast(
  deps: Pick<ToastDeps, 't' | 'showToast' | 'dismissToast'>,
  traceId: string,
): void {
  const submittingId = getToastId(TRADING_TOAST_IDS.TX_SUBMITTING, traceId);
  deps.dismissToast(submittingId);

  deps.showToast({
    id: getToastId(TRADING_TOAST_IDS.TX_CANCELLED, traceId),
    message: deps.t('toast.cancelled'),
    description: deps.t('toast.cancelledDescription'),
    variant: 'warning',
    duration: 5000,
  });
}

// Re-export TxTranslator so consumer hooks don't need a separate import line.
export type { TxTranslator };
